"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import { trackQuoteRequest } from "@/lib/analytics";

const managementFeatures = [
{
  icon: "📊",
  title: "Listing Optimization",
  desc: "Professional photos, keyword-rich descriptions, and competitive positioning to maximize visibility on Airbnb and VRBO."
},
{
  icon: "💰",
  title: "Dynamic Pricing",
  desc: "AI-powered pricing adjustments based on local demand, seasonality, events, and competitor rates. Average 25% revenue lift."
},
{
  icon: "💬",
  title: "24/7 Guest Messaging",
  desc: "Every guest inquiry, check-in question, and issue handled professionally around the clock. 5-star communication guaranteed."
},
{
  icon: "🔧",
  title: "Maintenance Coordination",
  desc: "Vetted local contractors on call. We handle everything under $500 with your approval. You\'re never blindsided."
},
{
  icon: "📸",
  title: "Professional Photography",
  desc: "Stunning photos that convert browsers into bookers. Included on onboarding for all full-management clients."
},
{
  icon: "📋",
  title: "Monthly Owner Reports",
  desc: "Detailed revenue reports, occupancy stats, guest feedback summaries, and expense breakdowns delivered every month."
}];


const feeBreakdown = [
{ range: "18%", label: "Standard", desc: "For properties with 15+ bookings/month" },
{ range: "22%", label: "Growth", desc: "For properties needing active optimization" },
{ range: "25%", label: "Premier", desc: "Full white-glove service, new listings" }];


export default function ManagementService() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add("revealed");});},
      { threshold: 0.08 }
    );
    const els = ref?.current?.querySelectorAll(".reveal-hidden");
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="management" ref={ref} className="section-padding bg-white scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-16">
          <div className="reveal-hidden">
            <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">Service B</span>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-5">
              Full-Service{" "}
              <span className="italic font-light text-gradient-gold">Management</span>
            </h2>
            <p className="text-navy/60 text-lg leading-relaxed mb-8">
              Truly hands-off ownership. We handle every aspect of your short-term rental — from listing creation to guest checkout — while you collect your monthly revenue report.
            </p>
            <div className="flex items-start gap-4 p-5 bg-sand rounded-2xl border border-sand-medium mb-6">
              <div className="w-12 h-12 rounded-xl bg-gold/15 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-navy mb-1">Average revenue increase: 20–35%</p>
                <p className="text-navy/55 text-sm">Our dynamic pricing and optimization strategy typically generates more gross revenue than self-management — even after our fee.</p>
              </div>
            </div>
            <Link href="/contact" onClick={() => trackQuoteRequest('management', 'services_management_quote')} className="btn-gold px-8 py-4 rounded-full font-semibold text-sm inline-flex items-center gap-2 group">
              Get a Management Quote
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          {/* Image */}
          <div className="reveal-hidden relative rounded-3xl overflow-hidden h-80 lg:h-96 shadow-luxury">
            <AppImage
              src="https://images.unsplash.com/photo-1678717295967-a36240155ec7"
              alt="Luxury vacation rental living room with ocean view perfectly staged for Airbnb guests"
              fill
              className="object-cover" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm font-semibold">This Month&apos;s Performance</span>
                  <span className="text-emerald-400 text-xs font-bold">+31% YoY</span>
                </div>
                <div className="flex gap-4">
                  <div>
                    <p className="text-gold font-bold text-lg">92%</p>
                    <p className="text-white/50 text-xs">Occupancy</p>
                  </div>
                  <div className="w-px bg-white/20" />
                  <div>
                    <p className="text-gold font-bold text-lg">$8,240</p>
                    <p className="text-white/50 text-xs">Gross Revenue</p>
                  </div>
                  <div className="w-px bg-white/20" />
                  <div>
                    <p className="text-gold font-bold text-lg">4.98</p>
                    <p className="text-white/50 text-xs">Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {managementFeatures?.map((feat, i) =>
          <div
            key={feat?.title}
            className="reveal-hidden bg-sand border border-sand-medium rounded-2xl p-6 hover:border-gold/30 hover:shadow-md transition-all duration-300 group"
            style={{ transitionDelay: `${i * 80}ms` }}>
            
              <div className="text-3xl mb-4">{feat?.icon}</div>
              <h4 className="font-heading text-lg text-navy mb-2">{feat?.title}</h4>
              <p className="text-navy/55 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: feat?.desc }} />
            </div>
          )}
        </div>

        {/* Fee breakdown */}
        <div id="fees" className="reveal-hidden bg-navy rounded-3xl p-8 sm:p-12">
          <div className="text-center mb-10">
            <h3 className="font-heading text-3xl text-white mb-3">Management Fee Structure</h3>
            <p className="text-white/55 text-sm max-w-lg mx-auto">
              18–25% of gross booking revenue. No hidden fees, no setup costs, no long-term contracts.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {feeBreakdown?.map((fee, i) =>
            <div
              key={fee?.range}
              className={`rounded-2xl p-6 text-center border ${
              i === 0 ?
              "bg-gold/10 border-gold/30" : "bg-white/5 border-white/10"}`
              }>
              
                <div className="font-heading text-4xl font-bold text-gold mb-2">{fee?.range}</div>
                <div className="font-semibold text-white mb-2">{fee?.label}</div>
                <div className="text-white/50 text-sm">{fee?.desc}</div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-white/50 text-sm">
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              No setup fees
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              No long-term contracts
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Cancel anytime with 30 days notice
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Free onboarding consultation
            </span>
          </div>
        </div>
      </div>
    </section>);

}